"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.findCurrentPhaseAndCheckpoint = findCurrentPhaseAndCheckpoint;
exports.getActionDuration = getActionDuration;
exports.getCurrentPhaseAndCheckpoint = getCurrentPhaseAndCheckpoint;
exports.getCurrentPhaseExpectedDuration = getCurrentPhaseExpectedDuration;
exports.getCurrentPhaseStartTime = getCurrentPhaseStartTime;
exports.getEstimatedPhaseAndCheckpointDurations = getEstimatedPhaseAndCheckpointDurations;
exports.getEstimatedTimeLeftOfUnvisitedPhases = getEstimatedTimeLeftOfUnvisitedPhases;
exports.getExpectedPhaseAndCheckpointDurations = getExpectedPhaseAndCheckpointDurations;
exports.getTimeBetween = getTimeBetween;
var _constants = require("../constants");
var _missionStateSelectors = require("../missionStateSelectors");
var _lodash = require("lodash");
const ONE_SECOND = 1000; // ms
const ONE_MINUTE = 60 * ONE_SECOND;

/**
 * An Array of all the action types that will be displayed as screens
 */
const SCREEN_ACTION_TYPES = ['IMAGE_SCREEN', 'VIDEO_SCREEN', 'NAVIGATION_SCREEN', 'CULMINATING_MOMENT_SCREEN', 'COLLABORATIVE_CULMINATING_MOMENT_SCREEN', 'MAP_SCREEN'];

/**
 * returns the duration for the current objective timer
 */
function getCurrentObjectiveTimerDuration(state) {
  const currentObjectiveTimerId = (0, _missionStateSelectors.getCurrentObjectiveTimerId)(state);
  const timer = (0, _missionStateSelectors.getTimer)(currentObjectiveTimerId)(state);
  return (0, _missionStateSelectors.getTimerDuration)(state, timer?.id);
}

/**
 * Returns the mission's visited phases and checkpoints
 */
function getVisitedPhasesAndCheckpoints(state) {
  return state ? state.launchpad.visitedPhasesAndCheckpoints : [];
}

/*
 * Gets the expected phase and checkpoint durations from the simulation then calculates the expected duration
 * for the current phase/checkpoint based on the times of visitedPhasesAndCheckpoints and the current screen
 */
function getExpectedPhaseAndCheckpointDurations(missionState, simulation, events) {
  // AUTOMATED TO DO - add/subtract time to expected durations if time added or subtracted from objective timer
  if (simulation.expectedPhaseAndCheckpointDurations.length < 1) {
    return simulation.expectedPhaseAndCheckpointDurations;
  }
  const visitedPhasesAndChecks = getVisitedPhasesAndCheckpoints(missionState);
  const currentScreen = (0, _missionStateSelectors.getScreenFromMission)(missionState);
  if (visitedPhasesAndChecks.length < 1 || !currentScreen) {
    return simulation.expectedPhaseAndCheckpointDurations;
  }
  const currentScreenDuration = getCurrentObjectiveTimerDuration(missionState);
  return calculateExpectedDurations(visitedPhasesAndChecks, events, currentScreen, simulation.expectedPhaseAndCheckpointDurations, simulation.actions, currentScreenDuration);
}

/**
 * Returns the current phase and checkpoints IDs and visited timestamps
 */
function getCurrentPhaseAndCheckpoint(missionState, simulation, events) {
  const visitedPhasesAndCheckpoints = getVisitedPhasesAndCheckpoints(missionState);
  const reversed = [...visitedPhasesAndCheckpoints].reverse();
  const currentCheck = reversed.find(value => simulation.actions[value._actionId]?.checkpoint != null);
  const currentPhase = reversed.find(value => simulation.actions[value._actionId]?.newPhase != null);
  return {
    currentCheck,
    currentPhase
  };
}

/**
 * Returns the total estimated time left for unvisited phases
 */
function getEstimatedTimeLeftOfUnvisitedPhases(missionState, simulation, events) {
  const expectedDurations = getExpectedPhaseAndCheckpointDurations(missionState, simulation, events);
  if (!expectedDurations) {
    return null;
  }
  const currentPhaseAndCheckpoint = getCurrentPhaseAndCheckpoint(missionState, simulation, events);
  const currentPhaseDurationIndex = expectedDurations.findIndex(duration => duration.phaseId === currentPhaseAndCheckpoint.currentPhase?._actionId);
  let timeLeft = 0;
  if (currentPhaseDurationIndex == null) {
    return null;
  } else if (currentPhaseDurationIndex === expectedDurations.length - 1) {
    return timeLeft;
  }
  for (let i = currentPhaseDurationIndex + 1; i < expectedDurations.length; i++) {
    timeLeft += expectedDurations[i].duration;
  }
  return timeLeft;
}

/**
 * Returns the start time of the current phase, or null if the current phase could not
 * be found
 */
function getCurrentPhaseStartTime(missionState, simulation, events) {
  const currentPhaseAndCheck = getCurrentPhaseAndCheckpoint(missionState, simulation, events);
  return currentPhaseAndCheck.currentPhase?.timestamp;
}

/**
 * Returns the expected duration for the current phase if it could be found.
 * Otherwise returns null
 */
function getCurrentPhaseExpectedDuration(missionState, simulation, events) {
  const currentPhaseAndCheck = getCurrentPhaseAndCheckpoint(missionState, simulation, events);
  const expectedDurations = getExpectedPhaseAndCheckpointDurations(missionState, simulation, events);
  if (!expectedDurations || !currentPhaseAndCheck.currentPhase) {
    return null;
  }
  const currentPhaseDuration = expectedDurations.find(duration => duration.phaseId === currentPhaseAndCheck.currentPhase?._actionId);
  return currentPhaseDuration?.duration;
}

/**
 * Returns the current phase and checkpoint IDs based on the currentScreenId
 * or null if the currentScreenId could not be found
 */
function findCurrentPhaseAndCheckpoint(estimatedDurations, currentScreenId) {
  for (let i = 0; i < estimatedDurations.length; i++) {
    const currentPhase = estimatedDurations[i];
    for (let j = 0; j < currentPhase.checkpoints.length; j++) {
      const currentCheck = currentPhase.checkpoints[j];
      for (let k = 0; k < currentCheck.screens.length; k++) {
        const screenIdOrIds = currentCheck.screens[k];
        if (typeof screenIdOrIds === 'string' && screenIdOrIds === currentScreenId || screenIdOrIds.includes(currentScreenId)) {
          return {
            phaseId: currentPhase.phaseId,
            checkpointId: currentCheck.id
          };
        }
      }
    }
    // check if screen is in current phase, but not in any of phases checkpoints
    const screenIndex = getScreenIndex(currentPhase.screens, currentScreenId);
    if (screenIndex > -1) {
      return {
        phaseId: currentPhase.phaseId,
        checkpointId: null
      };
    }
  }
  return {
    phaseId: null,
    checkpointId: null
  };
}

/**
 * Gets the duration of the given action.
 *
 * @returns {GetActionDurationResult.actionDuration} - the duration of the action
 * @returns {GetActionDurationResult.durationMetadata} - metadata about the duration of the action. In other words,
 * what the duration is made up of. See documentation in the TS definition file for more details.
 */
function getActionDuration(gameAction) {
  switch (gameAction.type) {
    case 'VIDEO_OVERLAY':
    case 'VOCAL_TRACK':
      {
        return {
          actionDuration: gameAction._mediaDuration,
          durationMetadata: {
            media: gameAction._mediaDuration
          }
        };
      }
    case 'VIDEO_SCREEN':
      {
        if (gameAction.looping) {
          return {
            actionDuration: gameAction.timeLimit,
            durationMetadata: {
              media: gameAction.timeLimit
            }
          };
        } else {
          if (gameAction._mediaDuration == null) {
            return {
              actionDuration: null,
              durationMetadata: {}
            };
          }
          return {
            actionDuration: gameAction._mediaDuration + gameAction.finalFrameDuration,
            durationMetadata: {
              media: gameAction._mediaDuration,
              finalFrameDuration: gameAction.finalFrameDuration
            }
          };
        }
      }
    case 'IMAGE_OVERLAY':
      {
        return {
          actionDuration: gameAction.duration,
          durationMetadata: {
            media: gameAction.duration
          }
        };
      }
    case 'IMAGE_SCREEN':
      {
        return {
          actionDuration: gameAction.timeLimit,
          durationMetadata: {
            media: gameAction.timeLimit
          }
        };
      }
    case 'START_TIMER':
      {
        return {
          actionDuration: gameAction.duration,
          durationMetadata: {
            media: gameAction.duration
          }
        };
      }
    case 'MAP_SCREEN':
    case 'CULMINATING_MOMENT_SCREEN':
    case 'QUESTION_SLOT_SCREEN':
    case 'NAVIGATION_SCREEN':
      {
        return {
          actionDuration: gameAction.timeLimit,
          durationMetadata: {}
        };
      }
    case 'COLLABORATIVE_CULMINATING_MOMENT_SCREEN':
      {
        const editingTime = gameAction.editingTime ?? 0;
        // padding for grading (the actual length will be dependent on the number of documents on fullState)
        const GRADING_DURATION = 5 * ONE_MINUTE;
        // padding for displaying results (the actual length will be dependent on the number of documents on fullState)
        const RESULTS_DURATION = 30 * ONE_SECOND;
        return {
          actionDuration: editingTime + GRADING_DURATION + RESULTS_DURATION,
          durationMetadata: {
            media: editingTime,
            gradingDuration: GRADING_DURATION,
            resultsDuration: RESULTS_DURATION
          }
        };
      }
    default:
      {
        return {
          actionDuration: null,
          durationMetadata: {}
        };
      }
  }
}

/**
 * Gets time between two timestamps excluding time the mission is not open
 */
function getTimeBetween(time1, time2, events) {
  if (!events) {
    return time2 - time1;
  }
  const eventsInRange = events.filter(event => {
    const eventTimestamp = new Date(event.timestamp).getTime();
    return Boolean(eventTimestamp >= time1 && eventTimestamp <= time2);
  });
  const sortedEvents = eventsInRange.sort((a, b) => new Date(a.timestamp).getMilliseconds() - new Date(b.timestamp).getMilliseconds());
  let totalMilliseconds = 0;
  let currentStart = time1;
  sortedEvents.forEach(event => {
    const eventTime = new Date(event.timestamp).getTime();
    if (event.type === _constants.MISSION_EVENTS.OPEN_MISSION) {
      currentStart = eventTime;
    } else if (event.type === _constants.MISSION_EVENTS.SAVE_FOR_LATER && currentStart) {
      totalMilliseconds += eventTime - currentStart;
      currentStart = null;
    }
  });
  if (currentStart) {
    totalMilliseconds += time2 - currentStart;
  }
  return totalMilliseconds;
}

/**
 * Iterates through the ordered screens to create a list of phaseIds with
 * checkpoints that occur during that phase and their durations.
 * @param {Simulation} simulation
 */
function getEstimatedPhaseAndCheckpointDurations(simulation) {
  const actions = simulation.actions;
  const initialAction = simulation.initialScreenActionId;
  if (!initialAction) {
    return [];
  }
  const phaseIds = new Set();
  let phasesAndCheckpoints = [];
  let currentPhase = null;
  let checkpoint = null;
  const orderedScreens = getScreenList(simulation, initialAction, false);
  const addCheckpoint = index => {
    if (!checkpoint || !currentPhase) {
      return;
    }
    const nonNullCurrentPhase = currentPhase;
    const screenSublist = [...orderedScreens].splice(checkpoint.index, index - checkpoint.index);
    const duration = getEstimatedLengthFromScreensList(screenSublist, actions);
    nonNullCurrentPhase.checkpoints.push({
      id: checkpoint?.id || 'unknown',
      duration: duration,
      screens: screenSublist
    });
  };
  const addPhase = index => {
    if (!currentPhase) {
      return;
    }
    const nonNullCurrentPhase = currentPhase;
    const screenSubList = [...orderedScreens].splice(currentPhase.index, index - currentPhase.index);
    const duration = getEstimatedLengthFromScreensList(screenSubList, actions);
    phasesAndCheckpoints.push({
      phaseId: nonNullCurrentPhase.phaseId,
      checkpoints: nonNullCurrentPhase.checkpoints,
      duration: duration,
      screens: screenSubList
    });
  };
  orderedScreens.forEach((screenIdOrIds, index) => {
    const screenIds = typeof screenIdOrIds === 'string' ? [screenIdOrIds] : screenIdOrIds;
    screenIds.forEach(id => {
      if (actions[id] && actions[id].newPhase != null && !phaseIds.has(id)) {
        phaseIds.add(id);
        if (checkpoint && currentPhase) {
          addCheckpoint(index);
          checkpoint = null;
        }
        if (currentPhase) {
          addPhase(index);
        }
        currentPhase = {
          phaseId: id,
          checkpoints: [],
          index: index
        };
      }
      if (actions[id] && actions[id].checkpoint != null && currentPhase) {
        if (checkpoint) {
          addCheckpoint(index);
        }
        checkpoint = {
          id,
          index
        };
      }
    });
  });
  if (checkpoint && currentPhase) {
    addCheckpoint(orderedScreens.length);
  }
  if (currentPhase) {
    addPhase(orderedScreens.length);
  }
  return phasesAndCheckpoints;
}

/**
 * Calculates the expected duration for the current phase/checkpoint
 * based on the times of visitedPhasesAndCheckpoints and the current screen
 */
function calculateExpectedDurations(visitedPhasesAndChecks, events, currentScreen, expectedDurations, actions, currentScreenDuration) {
  // get current phase and checkpoint timestamps
  const {
    currentCheck,
    currentPhase
  } = getCurrentPhaseAndCheckpointTimestamps(expectedDurations, currentScreen, visitedPhasesAndChecks);
  if (!currentPhase) {
    return expectedDurations;
  }

  // find current duration index and duration item for the current phase
  const phaseIndex = expectedDurations.findIndex(item => item.phaseId === currentPhase._actionId);
  // if current phase could not be found in expected durations, return default expected durations
  if (phaseIndex === -1) {
    return expectedDurations;
  }
  const phaseDuration = expectedDurations[phaseIndex];

  // if current checkpoint, find checkpoint duration index and duration item for the current checkpoint
  let checkIndex;
  let updatedCheckpointDuration;
  if (currentCheck) {
    const phaseCheckpoints = phaseDuration.checkpoints;
    checkIndex = phaseCheckpoints.findIndex(item => item.id === currentCheck._actionId);
    // if current checkpoint could not be found in the current phase's checkpoints, return the default expected durations
    if (checkIndex === -1) {
      return expectedDurations;
    }
    const checkpointDuration = phaseCheckpoints[checkIndex];

    // get expected duration for checkpoint
    const totalExpectedCheckpointTime = getExpectedDurationForScreenList(checkpointDuration.screens, currentCheck.timestamp, currentScreen, actions, events, currentScreenDuration);
    updatedCheckpointDuration = {
      ...expectedDurations[phaseIndex].checkpoints[checkIndex],
      duration: totalExpectedCheckpointTime
    };
  }
  const totalExpectedPhaseTime = getExpectedDurationForScreenList(phaseDuration.screens, currentPhase.timestamp, currentScreen, actions, events, currentScreenDuration);

  // return the updated PhaseDurations
  const checkpoints = expectedDurations[phaseIndex].checkpoints.slice();
  const updatedPhaseDuration = {
    ...expectedDurations[phaseIndex],
    checkpoints,
    duration: totalExpectedPhaseTime
  };
  if (currentCheck && checkIndex != null && updatedCheckpointDuration) {
    updatedPhaseDuration.checkpoints[checkIndex] = updatedCheckpointDuration;
  }
  const updatedPhaseDurations = [...expectedDurations];
  updatedPhaseDurations[phaseIndex] = updatedPhaseDuration;
  return updatedPhaseDurations;
}

/**
 * Finds the expected duration for the given screen list
 * First the completed portion of the phase/checkpoint is calculated.
 * This completed time is the actual length of the screens that have already been visited.
 * Then the expected duration of the rest of the phase/checkpoint is calculated.
 * The total of those two values is returned.
 * @param { ScreensList } screens the screens that we are testing
 * @param { number } startTime of the screen list ( i.e. start time of the phase of checkpoint)
 * @param { Screen } currentScreen from the screen list, where for that screen and after, all durations are estimated
 * @param { IdMap<GameActions>} actions all the actions from the simulation
 * @param { ?(MissionEvent[]) } events the events on the mission (whether it was closed/reopened)
 */
function getExpectedDurationForScreenList(screens, startTime, currentScreen, actions, events, currentScreenTimerDuration) {
  const currentScreenIndex = getScreenIndex(screens, currentScreen._actionId);
  const visitedScreenTime = getTimeBetween(startTime, currentScreen.timestamp, events);
  let expectedTimeOfUnvisited;
  if (currentScreenTimerDuration) {
    expectedTimeOfUnvisited = currentScreenTimerDuration;
    if (currentScreenIndex < screens.length - 1) {
      expectedTimeOfUnvisited += getEstimatedLengthFromScreensList([...screens].splice(currentScreenIndex + 1, screens.length), actions);
    }
  } else {
    expectedTimeOfUnvisited = getEstimatedLengthFromScreensList([...screens].splice(currentScreenIndex, screens.length), actions);
  }
  return visitedScreenTime + expectedTimeOfUnvisited;
}

/**
 * Returns the index of a a GameActionId in a ScreensList
 */
function getScreenIndex(screens, id) {
  return screens.findIndex(screenIdOrIds => {
    if (typeof screenIdOrIds === 'string') {
      return screenIdOrIds === id;
    } else {
      return screenIdOrIds.includes(id);
    }
  });
}

/**
 * Finds the current phase and checkpoints based on the currentScreen
 * then finds the timestamps that the phase/checkpoint started by finding
 * their associated ids in the visitedPhaseAndCheckpoints list.
 */
function getCurrentPhaseAndCheckpointTimestamps(expectedDurations, currentScreen, visitedPhasesAndChecks) {
  const {
    phaseId,
    checkpointId
  } = findCurrentPhaseAndCheckpoint(expectedDurations, currentScreen._actionId);
  const currentCheck = checkpointId ? visitedPhasesAndChecks.find(item => item._actionId === checkpointId) : null;
  const currentPhase = phaseId ? visitedPhasesAndChecks.find(item => item._actionId === phaseId) : null;
  return {
    currentCheck,
    currentPhase
  };
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *  /
 * * * ALL FUNCTIONS BELOW THIS POINT ARE DIRECT COPIES FROM SIMULATION-CREATOR algorithms.js * * /
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/**
 * Gets a rough estimate of the length of the provided `screensList`. It is not completely accurate as it only takes into account 1 screen from each
 * entry in `screensList`, and because some screens have timeLimits, but will not necessarily reach the full time to progress.
 */
function getEstimatedLengthFromScreensList(screensList, actions) {
  let accumulatedLength = 0;
  screensList.forEach(item => {
    const actionId = typeof item === 'string' ? item : item[0];
    const action = actions[actionId];
    if (action) {
      accumulatedLength += getActionDuration(action).actionDuration || 0;
    }
  });
  return accumulatedLength;
}

/**
 * Given an automated simulation and a currentScreenActionId, returns that current screen's subtree in the shape of an ordered list of screenIds.
 * The list is composed of single screenIds and sets of screen ids that could potentially be reached
 * from a previous screen or set of screens in the list.
 *
 * The list may include duplicate screen ids but may not include duplicate sequences of screen ids. For example:
 * Screen 1 -> Screen 2 -> Screen 5 -> Screen 3
 *          -> Screen 3 -> Screen 4 -> Screen 6 -> Screen 7
 * Where Screen 3 is included in the list twice, but its nested screens (Screen 4 -> Screen 6 -> Screen 7) is only included once
 * @param {Simulation} simulation
 * @param {PhaseId} currentPhaseId
 */
function getScreenList(simulation, firstScreen, phaseOnly = true) {
  const screensList = [];
  const screensSeen = new Set();
  if (!firstScreen) {
    return [];
  }
  let nextScreenIds = [firstScreen];
  while (nextScreenIds.length > 0) {
    if (nextScreenIds.length === 1) {
      screensList.push(nextScreenIds[0]);
    } else {
      screensList.push(nextScreenIds);
    }
    const newNextScreenIds = nextScreenIds.filter(screenId => {
      // Don't get next screen ids of a new phase
      if (screenId !== firstScreen && simulation.actions[screenId] && simulation.actions[screenId].newPhase != null && phaseOnly) {
        screensSeen.add(screenId);
        return false;
      }
      if (screensSeen.has(screenId)) {
        return false;
      } else {
        // Mark a new screen as seen
        screensSeen.add(screenId);
        return true;
      }
    });
    if (newNextScreenIds.length === 0) {
      break;
    }
    // Avoids infinite loop: Only continues getting next screen ids for new screenIds we haven't seen
    nextScreenIds = getNextScreenIdsFromScreens(newNextScreenIds, simulation.actions, simulation._id);
  }
  return screensList;
}

/**
 * Given a group of screenIds and the map of all actions on a simulation,
 * returns a group of unique screen ids which can occur right after given screens.
 * @param { ScreenActionId[] } screenIds
 * @param { IdMap<Action> } actions
 */
function getNextScreenIdsFromScreens(screenIds, actions, simulationId) {
  return screenIds.reduce((nextScreenIds, screenId) => {
    return (0, _lodash.union)(nextScreenIds, getNextScreenIdsFromAction(screenId, actions, simulationId));
  }, []);
}

/**
 * Given an action, determines all the next possible screen action ids spawned from that action.
 * Makes use of recursion to determine possible screens actions nested within the action EventResults.
 * @param {ActionId} actionId
 * @param {IdMap<Action>} actions
 */
function getNextScreenIdsFromAction(actionId, actions, simulationId) {
  const allNextActionIds = getNextActionIdsFromAction(actionId, actions);
  const allNextScreenIds = allNextActionIds.reduce((nextScreenIds, id) => {
    const action = actions[id];
    if (!action) {
      console.error(`unable to find actionId ${id} in simulation ${simulationId}`);
      return nextScreenIds;
    }
    if (SCREEN_ACTION_TYPES.includes(action.type)) {
      return (0, _lodash.union)(nextScreenIds, [id]);
    } else {
      return (0, _lodash.union)(nextScreenIds, getNextScreenIdsFromAction(id, actions, simulationId));
    }
  }, []);
  return allNextScreenIds;
}

/**
 * Returns a list of all the direct action ids that can be created by the given action.
 * Does NOT use recursion.
 * @param {ActionId} actionId
 * @param {IdMap<Action>} actions
 */
function getNextActionIdsFromAction(actionId, actions, determinerSubset) {
  const action = actions[actionId];
  if (!action) {
    return [];
  }
  let resultDeterminers = EVENT_RESULT_DETERMINERS;
  if (determinerSubset) {
    resultDeterminers = (0, _lodash.pick)(EVENT_RESULT_DETERMINERS, determinerSubset);
  }
  const eventDeterminerKeys =
  // $FlowIgnore[prop-missing] We know keys of `resultDeterminers` is a subset of keys of `action`, so this type is fine
  (0, _lodash.intersection)((0, _lodash.keys)(action), (0, _lodash.keys)(resultDeterminers));
  return eventDeterminerKeys.reduce((results, key) =>
  // $FlowIgnore[prop-missing] We limited `key` to be one of the keys of `action`, so we know that action has the expected key
  // $FlowIgnore[incompatible-call]
  (0, _lodash.union)(results, EVENT_RESULT_DETERMINERS[key](action) ?? []), []);
}

/**
 * A map with keys of all the possible fields on actions that yield EventResults.
 * Values of map consist of a function that returns an EventResult object given an action
 */
const EVENT_RESULT_DETERMINERS = {
  onStart: a => a.onStart,
  onEnd: a => a.onEnd,
  onComplete: a => a.onComplete,
  atTime: a => a.atTime.reduce((allTimeActions, time) => [...allTimeActions, ...time.actions], []),
  questionInfo: (a, optionIndex) => {
    if (optionIndex != null && a.questionInfo.options[optionIndex]) {
      return a.questionInfo.options[optionIndex].result || [];
    }
    return a.questionInfo.options.reduce((allOptionResults, option) => [...allOptionResults, ...option.result], []);
  },
  stationData: a => a.stationData.onComplete,
  onCrash: a => a.onCrash,
  onCheatDeath: a => a.onCheatDeath || [],
  onAllObjectsScanned: a => a.onAllObjectsScanned,
  onFailure: a => a.onFailure,
  onSuccess: a => a.onSuccess
};